import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import DiagnosisCodeSelection, { DiagnosisRow } from './DiagnosisCodeSelection';
import DiagnosisTypeSelection from './DiagnosisTypeSelection';
import OnboardingModal from './OnboardingModal';
import useDiagnosisData, { DIAGNOSIS_STATUS } from './useDiagnosisData';
import { useRouteMatch } from 'react-router-dom';
import { ROPatientCarePlanRoute } from '../../Interface';
import ROPatientCarePlanPageFooter from '../../Footer';
import CareplanContainer from '../../Container';
import { useCareplan } from './useCareplan';
import { CareplanBanners } from 'op-pages/RO/Careplan/Banner/Banner';
import { CAREPLAN_STATUS } from 'op-pages/RO/Careplan/Common';
import { GET_CAREPLAN_AND_DIAGNOSIS } from '../Queries';
import SkeletonTableRows from './SkeletonTableRows';

const AnimatedContainer = styled('div')({
  overflow: 'hidden',
  transition: 'none',
  maxHeight: '0px',
  padding: '4px',
  margin: '-2px',
  '&.expanded': {
    maxHeight: '1000px', // Adjust this value based on your content
  },
  '& .content': {
    opacity: 0,
    transform: 'translateY(200px)',
    transition: 'opacity .3s ease-in-out, transform .8s ease-in-out',
    margin: '-2px',
  },
  '&.expanded .content': {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

/**
 * SimpleDiagnosisSelection Component
 *
 * A component that allows users to select primary and metastasis diagnoses for a careplan.
 * It provides a toggle between primary and metastasis diagnosis types, and displays
 * a searchable grid of diagnosis codes for selection.
 */
const SimpleDiagnosisSelection = (): JSX.Element => {
  // Get patient and careplan IDs from the URL
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const { id: patientId, careplanId } = match.params;

  // State management for selected diagnoses and diagnosis type
  const [selectedPrimaryDiagnosis, setSelectedPrimaryDiagnosis] = useState<DiagnosisRow | null>(null);
  const [selectedMetastasisDiagnosis, setSelectedMetastasisDiagnosis] = useState<DiagnosisRow | null>(null);
  const [diagnosisType, setDiagnosisType] = useState<'metastasis' | 'primary' | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const [showPrimaryDiagnosisTable, setShowPrimaryDiagnosisTable] = useState<boolean>(false);
  const primaryErrorRef = useRef<HTMLDivElement>(null);
  const metastasisErrorRef = useRef<HTMLDivElement>(null);
  const metastasisContainerRef = useRef<HTMLDivElement>(null);
  const hasUserSelectedType = useRef<boolean>(false);

  // Fetch diagnosis data for both primary and metastasis diagnoses
  const {
    diagnosisList: diagnosisPrimaryReferenceList,
    careplanDiagnosis: careplanPrimaryDiagnosis,
    loading: primaryDiagnosisLoading,
  } = useDiagnosisData(patientId, careplanId, 'primary');

  const {
    diagnosisList: diagnosisMetastasisReferenceList,
    careplanDiagnosis: careplanMetastasisDiagnosis,
    loading: metastasisDiagnosisLoading,
  } = useDiagnosisData(patientId, careplanId, 'metastasis');

  // Hook for handling careplan diagnosis updates
  const { careplan, handleContinue } = useCareplan(patientId, careplanId);

  // Update selected primary diagnosis when careplan data changes and no metastasis diagnosis is selected
  useEffect(() => {
    if (
      diagnosisType === 'primary' &&
      !selectedPrimaryDiagnosis &&
      !selectedMetastasisDiagnosis &&
      !primaryDiagnosisLoading &&
      careplanPrimaryDiagnosis
    ) {
      setSelectedPrimaryDiagnosis(careplanPrimaryDiagnosis);
    }
  }, [careplanPrimaryDiagnosis, primaryDiagnosisLoading, selectedPrimaryDiagnosis, diagnosisType]);

  // Update selected metastasis diagnosis when careplan data changes
  useEffect(() => {
    // Only set metastasis diagnosis if user is working with metastasis type
    if (
      diagnosisType === 'metastasis' &&
      !selectedMetastasisDiagnosis &&
      !metastasisDiagnosisLoading &&
      careplanMetastasisDiagnosis?.code
    ) {
      setSelectedMetastasisDiagnosis(careplanMetastasisDiagnosis);
      setSelectedPrimaryDiagnosis(careplanMetastasisDiagnosis.relatedPrimaryDiagnosis || null);
      if (
        !careplanMetastasisDiagnosis.relatedPrimaryDiagnosis &&
        careplanMetastasisDiagnosis.status === DIAGNOSIS_STATUS.EXISTING
      ) {
        setShowPrimaryDiagnosisTable(false);
      } else if (
        careplanMetastasisDiagnosis.relatedPrimaryDiagnosis ||
        !(careplanMetastasisDiagnosis.status === DIAGNOSIS_STATUS.EXISTING)
      ) {
        setShowPrimaryDiagnosisTable(true);
      }
    }
  }, [careplanMetastasisDiagnosis, metastasisDiagnosisLoading, selectedMetastasisDiagnosis, diagnosisType]);

  // On initial load, Determine diagnosis type based on what's currently assigned to the careplan
  useEffect(() => {
    // Only auto-determine diagnosis type if user hasn't made a manual selection
    if (hasUserSelectedType.current) {
      return;
    }

    // Don't determine diagnosis type during loading states
    if (primaryDiagnosisLoading || metastasisDiagnosisLoading) {
      return;
    }

    if (!careplanPrimaryDiagnosis?.code && !careplanMetastasisDiagnosis?.code) {
      setDiagnosisType(null);
      return;
    }
    if (careplanPrimaryDiagnosis?.code && !careplanMetastasisDiagnosis?.code) {
      setDiagnosisType('primary');
      hasUserSelectedType.current = true; // Mark as selected since there's existing data
      return;
    }

    if (careplanMetastasisDiagnosis?.code) {
      setDiagnosisType('metastasis');
      hasUserSelectedType.current = true; // Mark as selected since there's existing data
      return;
    }
  }, [careplanPrimaryDiagnosis, careplanMetastasisDiagnosis, primaryDiagnosisLoading, metastasisDiagnosisLoading]);

  // Clear metastasis diagnosis when diagnosis type is primary
  useEffect(() => {
    if (diagnosisType === 'primary' && selectedMetastasisDiagnosis) {
      setSelectedMetastasisDiagnosis(null);
    }
  }, [diagnosisType, selectedMetastasisDiagnosis]);

  // Scroll to error when validation is true
  useEffect(() => {
    if (hasError) {
      scrollToError();
    }
  }, [hasError]);

  /**
   * Validates the current form state and returns an error message if invalid
   * @returns string | undefined - Error message if invalid, undefined if valid
   */
  const validateForm = useCallback((): string | undefined => {
    if (!diagnosisType) {
      return 'Please select a diagnosis type';
    }
    if (diagnosisType === 'primary' && !selectedPrimaryDiagnosis) {
      return 'Missing primary diagnosis code';
    }
    if (diagnosisType === 'metastasis' && !selectedMetastasisDiagnosis) {
      return 'Missing metastatic diagnosis code';
    }
    if (
      diagnosisType === 'metastasis' &&
      selectedMetastasisDiagnosis?.status !== DIAGNOSIS_STATUS.EXISTING &&
      !selectedPrimaryDiagnosis
    ) {
      return 'Missing related primary diagnosis code';
    }
    return undefined;
  }, [diagnosisType, selectedPrimaryDiagnosis, selectedMetastasisDiagnosis]);

  /**
   * Handles changes to the primary diagnosis selection
   * If diagnosis type is primary, clears metastasis diagnosis
   */
  const handlePrimaryDiagnosisChange = useCallback(
    (diagnosis: DiagnosisRow | null) => {
      setSelectedPrimaryDiagnosis(diagnosis);
      if (diagnosisType === 'primary') {
        setSelectedMetastasisDiagnosis(null);
      }
      setHasError(false);
    },
    [diagnosisType],
  );

  /**
   * Handles changes to the metastasis diagnosis selection
   */
  const handleMetastasisDiagnosisChange = useCallback((diagnosis: DiagnosisRow | null) => {
    setSelectedMetastasisDiagnosis(diagnosis);
    setHasError(false);
    // EXISTING mets: related primary; show primary table only if related primary is existing
    if (diagnosis?.status === DIAGNOSIS_STATUS.EXISTING) {
      setSelectedPrimaryDiagnosis(diagnosis.relatedPrimaryDiagnosis || null);
      setShowPrimaryDiagnosisTable(Boolean(diagnosis.relatedPrimaryDiagnosis));
    } else {
      // New mets: require user to pick a related primary
      setSelectedPrimaryDiagnosis(null);
      setShowPrimaryDiagnosisTable(true);
    }

    if (diagnosis && metastasisContainerRef.current) {
      // Small timeout to ensure container has expanded
      setTimeout(() => {
        metastasisContainerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }, 50);
    }
  }, []);

  /**
   * Handles changes to the diagnosis type selection (primary/metastasis)
   */
  const handleDiagnosisTypeChange = useCallback((newDiagnosisType: 'metastasis' | 'primary' | null) => {
    if (newDiagnosisType === null) return; // Prevent unselecting the current selection

    hasUserSelectedType.current = true;
    setDiagnosisType(newDiagnosisType);
    setSelectedPrimaryDiagnosis(null);
    setSelectedMetastasisDiagnosis(null);
    setHasError(false);
  }, []);

  /**
   * Scrolls to the error message when validation fails
   */
  const scrollToError = useCallback(() => {
    const errorRef = !selectedMetastasisDiagnosis
      ? metastasisErrorRef
      : !selectedPrimaryDiagnosis
      ? primaryErrorRef
      : null;
    errorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedMetastasisDiagnosis, selectedPrimaryDiagnosis]);

  /**
   * Handles the next button click, validating the form and proceeding if valid
   */
  const handleNext = useCallback(() => {
    const error = validateForm();

    if (error) {
      setHasError(true);
      return;
    }
    handleContinue(selectedPrimaryDiagnosis, selectedMetastasisDiagnosis);
  }, [validateForm, selectedPrimaryDiagnosis, selectedMetastasisDiagnosis, handleContinue]);

  const isSubmitted = Boolean(careplan?.careplan?.submittedAt);

  // Determine whether to prevent changing of the diagnosis if treatment has started.
  // This field is only populated for the currently selected diagnosis on load, as it requires an Iguana fetch to retrieve.
  // So this is only to protect from the case where the Dx was created and treatment has started under THIS careplan.
  // The rules around selceting an 'Existing' Dx for a new careplan should handle all other cases.
  // Memo to prevent unneccesary rerenders.
  const hasTreatmentStarted = useMemo(() => {
    if (careplan?.careplan?.careplanStatus === CAREPLAN_STATUS.draft) {
      return false;
    }
    return Boolean(
      careplanPrimaryDiagnosis?.hasTreatmentStarted === true ||
        careplanMetastasisDiagnosis?.hasTreatmentStarted === true,
    );
  }, [
    careplan?.careplan?.careplanStatus,
    careplanMetastasisDiagnosis?.hasTreatmentStarted,
    careplanPrimaryDiagnosis?.hasTreatmentStarted,
  ]);

  const isLoadingDiagnosis = primaryDiagnosisLoading || metastasisDiagnosisLoading;

  return (
    <Stack direction="column" sx={{ width: 'calc(100vw - 240px)  !important', overflowX: 'hidden' }}>
      <OnboardingModal />
      <CareplanBanners
        data={careplan}
        refetchQueriesList={[{ query: GET_CAREPLAN_AND_DIAGNOSIS, variables: { careplanId } }]}
      />

      <CareplanContainer>
        <Stack width="100%">
          <Stack gap={2} width="100%" maxWidth="1500px" margin="auto">
            {/* Diagnosis type selection toggle */}
            {isLoadingDiagnosis ? (
              <SkeletonTableRows rowCount={6} />
            ) : (
              <>
                <DiagnosisTypeSelection
                  selectedPrimaryDiagnosis={selectedPrimaryDiagnosis}
                  isSubmitted={isSubmitted}
                  type={diagnosisType}
                  onSelectionChange={handleDiagnosisTypeChange}
                />
                {/* Primary diagnosis selection grid */}
                {diagnosisType && (
                  <>
                    <DiagnosisCodeSelection
                      isSubmitted={isSubmitted}
                      hasTreatmentStarted={hasTreatmentStarted}
                      data={
                        diagnosisType === 'primary' ? diagnosisPrimaryReferenceList : diagnosisMetastasisReferenceList
                      }
                      title={diagnosisType === 'primary' ? 'Primary Diagnosis Code' : 'Metastatic Diagnosis Code'}
                      type={diagnosisType}
                      selectedDiagnosis={
                        diagnosisType === 'primary' ? selectedPrimaryDiagnosis : selectedMetastasisDiagnosis
                      }
                      onSelectionChange={
                        diagnosisType === 'primary' ? handlePrimaryDiagnosisChange : handleMetastasisDiagnosisChange
                      }
                      errorString={
                        hasError &&
                        ((diagnosisType === 'primary' && !selectedPrimaryDiagnosis) ||
                          (diagnosisType === 'metastasis' && !selectedMetastasisDiagnosis))
                          ? `Missing ${diagnosisType === 'metastasis' ? 'metastatic' : 'primary'} diagnosis code`
                          : undefined
                      }
                      errorRef={
                        hasError &&
                        ((diagnosisType === 'primary' && !selectedPrimaryDiagnosis) ||
                          (diagnosisType === 'metastasis' && !selectedMetastasisDiagnosis))
                          ? diagnosisType === 'primary'
                            ? primaryErrorRef
                            : metastasisErrorRef
                          : undefined
                      }
                      currentDiagnosisType={diagnosisType}
                    />
                    {/* Primary Diagnosis for mets (shown only when metastasis code is selected) */}
                    <AnimatedContainer
                      ref={metastasisContainerRef}
                      className={
                        diagnosisType === 'metastasis' && selectedMetastasisDiagnosis?.code && showPrimaryDiagnosisTable
                          ? 'expanded'
                          : ''
                      }>
                      <div className="content">
                        {diagnosisType === 'metastasis' && selectedMetastasisDiagnosis && showPrimaryDiagnosisTable && (
                          <DiagnosisCodeSelection
                            isSubmitted={isSubmitted}
                            hasTreatmentStarted={hasTreatmentStarted}
                            data={diagnosisPrimaryReferenceList}
                            title="Related Primary Diagnosis"
                            type="primary"
                            selectedDiagnosis={selectedPrimaryDiagnosis}
                            selectedMetastasisDiagnosis={selectedMetastasisDiagnosis}
                            onSelectionChange={handlePrimaryDiagnosisChange}
                            errorString={
                              hasError && !selectedPrimaryDiagnosis
                                ? 'Missing related primary diagnosis code'
                                : undefined
                            }
                            errorRef={hasError && !selectedPrimaryDiagnosis ? primaryErrorRef : undefined}
                            currentDiagnosisType={diagnosisType}
                          />
                        )}
                      </div>
                    </AnimatedContainer>
                  </>
                )}
              </>
            )}
          </Stack>
        </Stack>
      </CareplanContainer>
      {/* Footer with next button */}
      <ROPatientCarePlanPageFooter backHidden nextDisabled={!diagnosisType} onNext={handleNext} />
    </Stack>
  );
};

export default SimpleDiagnosisSelection;
